import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { CheckCircle2, Compass, ArrowRight } from "lucide-react";

const paths = [
  {
    id: "ya-lo-vendo",
    icon: CheckCircle2,
    tag: "Ya lo tengo claro",
    title: "Vendo videopodcast y necesito un proveedor serio",
    body:
      "Ya tienes el servicio en tu catálogo (o un cliente que lo pide mañana) y lo que te falta es alguien que cumpla plazos, no te haga quedar mal y entregue con nivel. Eso es exactamente lo que hacemos.",
    bullets: [
      "Calendario de grabaciones y fechas de entrega firmadas",
      "Un interlocutor único: sin cadenas de freelance",
      "Entrega con tu marca, tus intros y tu línea gráfica",
    ],
    cta: "Ver cómo trabajamos",
    href: "#como-funciona",
    accent: "from-galvani-blue to-galvani-purple",
  },
  {
    id: "no-lo-vendo",
    icon: Compass,
    tag: "Aún no lo ofrezco",
    title: "No sabía que podía vender esto a mis clientes",
    body:
      "Tus clientes ya te piden \"más contenido\" y \"más autoridad\". El videopodcast es el formato que resuelve las dos cosas de golpe, y es de los pocos servicios que puedes vender con recurrencia mensual sin ampliar tu equipo.",
    bullets: [
      "Un servicio nuevo que encaja en cuentas que ya tienes",
      "Recurrencia mensual, no proyectos de un solo pago",
      "Te ayudamos a preparar el argumentario para venderlo",
    ],
    cta: "Ver la oportunidad",
    href: "#oportunidad",
    accent: "from-galvani-cyan to-galvani-blue",
  },
];

export default function AgenciesPaths() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="section-spacing bg-galvani-bg" data-testid="agencies-paths">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-14"
        >
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-galvani-purple font-body font-semibold mb-4">
            ¿En qué punto estás?
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-galvani-text mb-5">
            Trabajamos con dos tipos de agencia.{" "}
            <span className="gradient-text">Elige la tuya</span>
          </h2>
          <p className="font-body text-base text-galvani-textLight leading-relaxed">
            Da igual si vienes con un cliente firmado o si acabas de descubrir que esto se puede
            vender: el acuerdo se adapta a tu punto de partida.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {paths.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.15 + i * 0.15 }}
              className="relative rounded-3xl bg-white border border-slate-200 shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 overflow-hidden"
              data-testid={`agencies-path-${p.id}`}
            >
              <div className={`h-1.5 w-full bg-gradient-to-r ${p.accent}`} />
              <div className="p-7 md:p-9">
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${p.accent} flex items-center justify-center shadow-md`}
                  >
                    <p.icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.18em] font-body font-semibold text-galvani-textLight">
                    {p.tag}
                  </span>
                </div>

                <h3 className="font-heading font-bold text-xl md:text-2xl text-galvani-text mb-3">
                  {p.title}
                </h3>
                <p className="font-body text-sm text-galvani-textLight leading-relaxed mb-6">
                  {p.body}
                </p>

                <ul className="space-y-2.5 mb-7">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle2 className="w-3 h-3 text-green-600" />
                      </span>
                      <span className="font-body text-sm text-galvani-text">{b}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={p.href}
                  className="inline-flex items-center gap-2 font-body font-semibold text-sm text-galvani-purple hover:gap-3 transition-all"
                  data-testid={`agencies-path-cta-${p.id}`}
                >
                  {p.cta}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
